import { useCallback, useImperativeHandle, useState } from 'react';

import { checkDose, checkMedicationEntry, checkMedicationName } from '@psy/validations';

import { useLanguage } from '../../contexts/language';
import { useField, validateAll } from '../../hooks/field';
import { useRecordChanges } from '../../hooks/record-changes';
import { useVisit } from '../../hooks/visit';

import { Button } from '../button';
import { PageTitle } from '../page-title';
import { StepActions } from '../step-actions';
import { TextField } from '../text-field';

import styles from './style.module.css';

// Step 5: the patient's medication list, with what they write for each one
// today. What that note means is still to be agreed with the clinician (O08),
// so it is free text, and an empty note is simply no note.
//
// The list is the patient's own and lasts across days: a medication added
// here is on the list tomorrow too, with nothing written for it, which never
// implies it was taken. Removing one takes it off the days to come only.
//
// The medications shown are the record day's: the current list, which a
// past day offers too, so one added today, or from the past day itself, can
// be written there at once (#144); and on a past day a medication removed
// since still shows, with what was written for it, but cannot be removed
// again.
//
// The notes are one state object rather than a field each, because the
// number of medications changes while the step is open and hooks cannot be
// added in a loop.
export function MedicationStep({ ref, record, medications, onBack, onSkip, onMove, onSaving, onStopped, onDone, onListChanged }) {
  const { text, translate } = useLanguage();
  const { saveEntries, addMedication, archiveMedication } = useRecordChanges();
  const capture = useVisit();
  const [ busy, setBusy ] = useState(false);
  const [ notes, setNotes ] = useState(function() {
    const saved = {};

    record.medicationEntries.forEach(function({ medication: { id }, entry }) {
      saved[ id ] = entry;
    });

    return saved;
  });
  const [ errors, setErrors ] = useState({});
  // Each removal under way or that went wrong, by medication: pending while
  // its request runs, so it cannot be sent twice; the sentence it failed
  // with; or removed when the removal went through but the list could not be
  // read again, which must not read as a failed removal.
  const [ removals, setRemovals ] = useState({});
  const name = useField({ initial: '', check: checkMedicationName });
  const dose = useField({ initial: '', check: checkDose });

  const onAdd = useCallback(function() {
    if (validateAll([ name, dose ]) === false) {
      return null;
    }

    const owns = capture();

    return addMedication({ name: name.value, doseMg: dose.value }).then(function() {
      if (owns() === false) {
        return null;
      }

      name.onChange('');
      dose.onChange('');

      return onListChanged();
    }).catch(function(failed) {
      if (owns() === false) {
        return;
      }

      name.refuse(failed.message);
    });
  }, [ name, dose, addMedication, onListChanged, capture ]);

  // Saves what was written, then goes to `to`, or to the next step when to
  // is null, as Next does; the bar calls it with the step it chose (#158).
  const submit = useCallback(function(to) {
    const refused = {};

    medications.forEach(function({ id }) {
      let note = '';

      if (typeof notes[ id ] === 'string') {
        note = notes[ id ];
      }

      const refusal = checkMedicationEntry(note);

      if (refusal != null) {
        refused[ id ] = refusal;
      }
    });

    setErrors(refused);

    if (Object.keys(refused).length > 0) {
      document.getElementById(`medication-${ Object.keys(refused)[ 0 ] }`).focus();

      return null;
    }

    if (medications.length === 0) {
      return onMove(to);
    }

    setBusy(true);

    const move = onSaving(to);
    const owns = capture();
    const entries = medications.map(function({ id }) {
      let entry = '';

      if (typeof notes[ id ] === 'string') {
        entry = notes[ id ];
      }

      return { medicationId: id, entry };
    });

    return saveEntries({ date: record.date, entries }).then(function() {
      if (owns() === false) {
        return onStopped(move);
      }

      return onDone(move);
    }).catch(function(failed) {
      onStopped(move);

      if (owns() === false) {
        return;
      }

      setBusy(false);
      setErrors({ [ medications[ 0 ].id ]: failed.message });
    });
  }, [ medications, notes, record, saveEntries, onMove, onSaving, onStopped, onDone, capture ]);

  const onSubmit = useCallback(function(event) {
    event.preventDefault();
    submit(null);
  }, [ submit ]);

  useImperativeHandle(ref, function() {
    return { submit };
  }, [ submit ]);

  const rows = medications.map(function({ id, name: medication, doseMg, archived }) {
    let label = medication;

    if (doseMg != null) {
      label = `${ medication } · ${ doseMg } mg`;
    }

    let value = '';

    if (typeof notes[ id ] === 'string') {
      value = notes[ id ];
    }

    const onNote = function(next) {
      setNotes(function(current) {
        return { ...current, [ id ]: next };
      });
    };

    const settle = function(state) {
      setRemovals(function(current) {
        return { ...current, [ id ]: state };
      });
    };

    const onRemove = function() {
      settle({ pending: true });

      return archiveMedication(id).then(function() {
        return onListChanged().then(function() {
          settle({});
        }, function() {
          settle({ removed: true });
        });
      }, function(failed) {
        settle({ failed: failed.message });
      });
    };

    // Container defaulting: a medication never removed and one whose removal
    // settled cleanly have the same empty state.
    const removal = removals[ id ] || {};

    let remove = <button className={ styles.remove } type='button' onClick={ onRemove } disabled={ removal.pending === true }>{ text.removeMedication.replace('{name}', medication) }</button>;
    let removeMessage = null;

    if (typeof removal.failed === 'string') {
      removeMessage = <span className={ styles.removeError } role='alert'>{ translate(removal.failed) }</span>;
    }

    if (archived === true || removal.removed === true) {
      remove = null;
    }

    if (removal.removed === true) {
      removeMessage = <span className={ styles.removeError } role='alert'>{ text.removedNotRefreshed }</span>;
    }

    return (
      <div key={ id } className={ styles.medication }>
        <TextField id={ `medication-${ id }` } label={ label } value={ value } onChange={ onNote } error={ translate(errors[ id ]) }/>

        { remove }

        { removeMessage }
      </div>
    );
  });

  let list = <p className={ styles.none }>{ text.noMedications }</p>;

  if (rows.length > 0) {
    list = <div className={ styles.list }>{ rows }</div>;
  }

  return (
    <form className={ styles.form } onSubmit={ onSubmit } noValidate>
      <PageTitle title={ text.medicationQuestion } intro={ text.medicationHint }/>

      { list }

      <fieldset className={ styles.add }>
        <legend className={ styles.addTitle }>{ text.addMedication }</legend>

        <div className={ styles.addFields }>
          <TextField ref={ name.ref } id='medication-name' label={ text.medicationName } value={ name.value } onChange={ name.onChange } onBlur={ name.onBlur } error={ translate(name.error) }/>

          <TextField ref={ dose.ref } id='medication-dose' label={ text.dose } value={ dose.value } onChange={ dose.onChange } onBlur={ dose.onBlur } error={ translate(dose.error) } inputMode='decimal'/>
        </div>

        <Button secondary onClick={ onAdd }>{ text.add }</Button>
      </fieldset>

      <StepActions onBack={ onBack } submitLabel={ text.next } busy={ busy } skipLabel={ text.skip } onSkip={ onSkip }/>
    </form>
  );
}
